import { getInternalApiUrl } from './internal-api-url';

const getApiBaseUrl = () => {
  // Server-side: use internal URL (INTERNAL_API_URL, falling back to API_URL) if available
  // Client-side: proxy through Next.js to hide the backend URL
  if (typeof window === 'undefined') {
    return getInternalApiUrl();
  }
  return '/api/proxy';
};

/** Default ISR window for tagged public fetches. */
const DEFAULT_REVALIDATE_SECS = 30;

/**
 * Long ISR window for content that is genuinely near-static AND is invalidated
 * on write by revalidationService.notify() -> /api/revalidate -> revalidateTag.
 * For those, the timer is only a backstop against a missed notify, so polling on
 * a 30s loop just manufactures load: banners changed a handful of times ever, but
 * a 30s window costs ~2,880 API calls a day and turns any brief API outage into a
 * wall of identical upstream errors, one every 30 seconds, for as long as it lasts.
 */
const STATIC_REVALIDATE_SECS = 3600;

async function fetchApi<T>(
  endpoint: string,
  params?: Record<string, string | undefined>,
  tags?: string[],
  revalidate: number = DEFAULT_REVALIDATE_SECS,
): Promise<T> {
  const baseUrl = getApiBaseUrl();
  const base = typeof window === 'undefined' ? undefined : window.location.origin;
  const url = new URL(`${baseUrl}${endpoint}`, base);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined) url.searchParams.set(k, v);
    });
  }
  const fetchOptions: RequestInit = typeof window === 'undefined'
    ? { next: { revalidate, tags } }
    : {};
  try {
    const response = await fetch(url.toString(), fetchOptions);
    if (!response.ok) throw new Error(`API Error: ${response.status} ${response.statusText}`);
    return response.json();
  } catch (error) {
    // A 404 is an expected outcome for public lookups (e.g. an invite-only or
    // unpublished event reached via its bare slug) — warn so genuine failures
    // (network errors, 5xx) still stand out at error level.
    const isNotFound = error instanceof Error && error.message.startsWith('API Error: 404');
    if (isNotFound) console.warn(`Not found: ${url}`);
    else console.error(`Failed to fetch ${url}:`, error);
    throw error;
  }
}

export interface ExamType {
  id: string;
  name: string;
  slug: string;
  // Seeded copy (packages/database/prisma/copy/v1-copy.ts) — real marketing
  // blurb per family, never invented client-side.
  description: string | null;
  icon: string | null;
  color: string | null;
  order: number;
  _count: { modules: number; courses: number };
}

export interface TestModule {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  // Short student-facing notice — only populated on single-module detail
  // lookups (e.g. getModuleBySlug), omitted from list responses.
  note?: string | null;
  coverImageUrl: string | null;
  ogImageUrl: string | null;
  isFree: boolean;
  price: number;
  basePrice: number;
  promoPrice: number | null;
  // Latihan tier signal (MINI/PARTISI/FULL) — see docs/revisi-260711-founder-notes.md §3.2.
  // Nullable/absent on any payload shape that predates this field; never assume a default.
  reportType?: 'FULL_EXAM' | 'SECTION_DRILL' | 'QUICK_PRACTICE' | null;
  // Progressive unlock (P-04) — id of the module the viewer must complete first.
  // Null/absent means no prerequisite. Resolve against the viewer's own
  // completed-module set (useOwnedItems().completedModuleIds), never trust a
  // server-computed "locked" flag here — this payload is publicCache(300) and
  // must stay identical for every viewer.
  unlockAfterModuleId?: string | null;
  tests: { id: string; durationMins: number }[];
  examTypes: { id: string; name: string; slug: string; icon: string | null; color: string | null }[];
  createdAt: string;
  updatedAt: string;
}

export interface Course {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  shortDescription: string | null;
  coverImageUrl: string | null;
  ogImageUrl: string | null;
  isFree: boolean;
  totalLessons: number;
  totalDurationMins: number;
  basePrice: number | null;
  promoPrice: number | null;
  examTypes: { id: string; name: string; slug: string; icon: string | null; color: string | null }[];
  subjects: { id: string; name: string; slug: string }[];
  chapters: {
    id: string;
    title: string;
    orderIndex: number;
    lessons: { id: string; title: string; type: string; durationMins: number | null; isFree: boolean; orderIndex: number }[];
  }[];
  createdAt: string;
}

export interface ModuleFilters {
  examTypeSlug?: string;
  isFree?: string;
  subjectSlug?: string;
}

export interface CourseFilters {
  examTypeSlug?: string;
  isFree?: string;
  subjectSlug?: string;
}

export interface SegmentExamType {
  id: string;
  slug: string;
  name: string;
  icon: string | null;
  color: string | null;
}

export const catalogApi = {
  getExamTypes: () => fetchApi<ExamType[]>('/catalog/exam-types'),
  // Segment-resolved, ordered, visible exam types — public, no auth needed.
  // `segment` is the AudienceSegment slug (SISWA | MAHASISWA | UMUM).
  getSegmentPolicy: (segment: string) =>
    fetchApi<SegmentExamType[]>('/catalog/segment-policy', { segment }),
  getProducts: () => fetchApi<ProductCatalog>('/catalog/products'),
};

// GET /v1/catalog/products — the whole public catalog in one slim response (5 min cache).
// Exam types carry only name + slug here, no icon or colour: take those from getExamTypes().
type ProductExamType = { name: string; slug: string };

export interface ProductCatalog {
  modules: {
    id: string;
    title: string;
    slug: string;
    description: string | null;
    isFree: boolean;
    price: number;
    promoPrice: number | null;
    testCount: number;
    examTypes: ProductExamType[];
  }[];
  courses: {
    id: string;
    title: string;
    slug: string;
    description: string | null;
    shortDescription: string | null;
    isFree: boolean;
    basePrice: number | null;
    promoPrice: number | null;
    totalLessons: number;
    totalDurationMins: number;
    examTypes: ProductExamType[];
  }[];
  bundles: {
    id: string;
    title: string;
    slug: string;
    description: string | null;
    price: number;
    promoPrice: number | null;
    moduleCount: number;
    examTypes: ProductExamType[];
  }[];
  liveEvents: {
    id: string;
    title: string;
    slug: string;
    description: string | null;
    startAt: string;
    endAt: string;
    status: string;
    pricing: { basePrice: number; promoPrice: number | null; effectivePrice: number } | null;
  }[];
  blogPosts: {
    id: string;
    title: string;
    slug: string;
    excerpt: string | null;
    category: { name: string; slug: string } | null;
    publishedAt: string | null;
    readingTimeMins: number;
  }[];
}

export const testModuleApi = {
  getPublishedModules: (filters?: ModuleFilters) =>
    fetchApi<TestModule[]>('/test/modules', filters as Record<string, string | undefined>),
  getFreeModules: () => fetchApi<TestModule[]>('/test/modules/free'),
  getModuleBySlug: (slug: string) =>
    fetchApi<TestModule>(`/test/modules/slug/${slug}`),
};

// ── Bundles (Paket Lengkap) ─────────────────────────────────

export interface Bundle {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  coverImageUrl: string | null;
  ogImageUrl: string | null;
  isPublished: boolean;
  price: number;
  basePrice: number;
  promoPrice: number | null;
  moduleCount: number;
  modules: {
    id: string;
    title: string;
    slug: string;
    description: string | null;
    basePrice: number;
    promoPrice: number | null;
    effectivePrice: number;
  }[];
  examTypes: { id: string; name: string; slug: string; icon: string | null; color: string | null }[];
  educationLevel: { id: string; name: string; slug: string } | null;
  createdAt: string;
  updatedAt: string;
}

// Backend only exposes a list endpoint (GET /v1/test/bundles) — there is no
// slug-detail route, so getBundleBySlug fetches the list and filters client-side.
export const bundleApi = {
  getPublishedBundles: () => fetchApi<Bundle[]>('/test/bundles'),
  getBundleBySlug: async (slug: string): Promise<Bundle | null> => {
    const bundles = await fetchApi<Bundle[]>('/test/bundles');
    return bundles.find((b) => b.slug === slug) ?? null;
  },
};

export const courseApi = {
  getPublishedCourses: (filters?: CourseFilters) =>
    fetchApi<Course[]>('/courses', filters as Record<string, string | undefined>),
  getCourseBySlug: (slug: string) =>
    fetchApi<Course>(`/courses/${slug}`),
};

export interface BannerData {
  id: string;
  title: string;
  subtitle?: string | null;
  ctaText?: string | null;
  ctaUrl?: string | null;
  bgColor?: string | null;
  bgGradient?: string | null;
  imageUrl?: string | null;
  textColor: string;
  imageOnly: boolean;
  type: 'STANDARD' | 'COUNTDOWN';
  targetDate?: string | null;
  priority: number;
}

export const bannerApi = {
  // Banners are invalidated on write (cached-banner.repository.ts calls
  // revalidationService.notify('banners') on create/update/delete), so an edit
  // still goes live immediately — see STATIC_REVALIDATE_SECS for why the timer
  // is deliberately long rather than 30s.
  getActive: (location: string, audience?: string) =>
    fetchApi<BannerData[]>('/banners', { location, audience }, ['banners'], STATIC_REVALIDATE_SECS),
};

// ── Blog ─────────────────────────────────────────────────────

export interface BlogPostListItem {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  coverImage: { id: string; url: string; thumbnailUrl: string | null } | null;
  ogImageUrl: string | null;
  status: string;
  publishedAt: string | null;
  author: { id: string; displayName: string | null };
  category: { id: string; name: string; slug: string; color: string | null } | null;
  tags: { id: string; name: string; slug: string; color: string | null }[];
  isFeatured: boolean;
  readingTimeMins: number;
  viewCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface BlogPostDetail extends BlogPostListItem {
  content: string;
  metaTitle: string | null;
  metaDescription: string | null;
  ogImageUrl: string | null;
  canonicalUrl: string | null;
  allowComments: boolean;
  author: { id: string; displayName: string | null; avatarUrl: string | null };
}

export interface PaginatedBlogPosts {
  data: BlogPostListItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  color: string | null;
  _count?: { posts: number };
}

export const blogApi = {
  getPosts: (params?: { page?: string; category?: string; tag?: string; search?: string }) =>
    fetchApi<PaginatedBlogPosts>('/blog', params),
  getFeatured: (limit?: string) =>
    fetchApi<BlogPostListItem[]>('/blog/featured', { limit }),
  getCategories: () =>
    fetchApi<BlogCategory[]>('/blog/categories'),
  getPostBySlug: (slug: string) =>
    fetchApi<BlogPostDetail>(`/blog/post/${slug}`),
};

// ── Live Events ─────────────────────────────────────────────

export interface LiveEventPublic {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  status: string;
  startMode: string;
  startAt: string;
  endAt: string;
  registrationOpenAt: string;
  registrationCloseAt: string;
  isFree: boolean;
  maxParticipants: number | null;
  coverImageUrl: string | null;
  ogImageUrl: string | null;
  prizes: { minRank: number; maxRank: number; description: string; imageUrl?: string }[] | null;
  category: { id: string; name: string; slug: string; color: string | null; icon: string | null };
  testModule: { id: string; title: string; slug: string };
  _count: { registrations: number };
  pricing: { basePrice: number; promoPrice: number | null; effectivePrice: number; currency: string } | null;
  // Raw scalar, passed through by the API's mapEventCoverPublic on every public event
  // surface (listPublic, getFeatured, getBySlug) — not stripped, unlike accessCode. A
  // private-but-catalog-listed event (see LiveEvent.isPubliclyListed) has isPublished:false
  // while still appearing here. Use it to derive invite-only card treatment.
  isPublished: boolean;
  // Only computed by getBySlug (single-event detail) — listPublic/getFeatured don't attach
  // it. `!isPublished` derives the same thing where this is absent (see isPublished above).
  requiresAccessCode?: boolean;
}

export interface LiveEventCategory {
  id: string;
  name: string;
  slug: string;
  icon: string | null;
  color: string | null;
}

export const liveEventApi = {
  getAll: (params?: { categoryId?: string; status?: string; limit?: string; page?: string }) =>
    fetchApi<{ data: LiveEventPublic[]; total: number; page: number; limit: number }>(
      '/live-events',
      params as Record<string, string | undefined>,
    ),
  getUpcoming: () =>
    fetchApi<{ data: LiveEventPublic[] }>('/live-events', { limit: '10' }),
  getFeatured: () =>
    fetchApi<LiveEventPublic[]>('/live-events/featured'),
  getBySlug: (slug: string) =>
    fetchApi<LiveEventPublic>(`/live-events/slug/${slug}`),
  getCategories: () =>
    fetchApi<LiveEventCategory[]>('/live-events/categories'),
};

export type PublicSettings = Record<string, string | number | boolean | unknown[] | Record<string, unknown>>;

export const settingsApi = {
  getPublic: () => fetchApi<PublicSettings>('/settings/public'),
};

export interface LegalDocument {
  id: string;
  type: 'PRIVACY_POLICY' | 'TERMS_AND_CONDITIONS';
  title: string;
  content: string;
  version: number;
  effectiveAt: string;
  isActive: boolean;
}

export const legalApi = {
  getPrivacyPolicy: () => fetchApi<LegalDocument>('/legal/privacy-policy', undefined, ['legal']),
  getTermsAndConditions: () => fetchApi<LegalDocument>('/legal/terms-and-conditions', undefined, ['legal']),
  getRefundPolicy: () => fetchApi<LegalDocument>('/legal/refund-policy', undefined, ['legal']),
};

export interface FaqItem {
  id: string;
  question: string;
  answer: string;
  sortOrder: number;
}

export interface FaqCategoryWithFaqs {
  id: string;
  name: string;
  slug: string;
  sortOrder: number;
  faqs: FaqItem[];
}

export const faqApi = {
  getPublished: () => fetchApi<FaqCategoryWithFaqs[]>('/faq', undefined, ['faq']),
};

export type ReferralProgramInfo =
  | { active: false }
  | {
      active: true;
      minQualifyingAmount: number;
      referrer: { discountType: string; discountValue: number; currency: string; voucherExpiresInDays: number };
      referee: { discountType: string; discountValue: number; currency: string; voucherExpiresInDays: number };
    };

export const referralApi = {
  /** Live reward config, so marketing copy never drifts from the admin panel. */
  getProgramInfo: () =>
    fetchApi<ReferralProgramInfo>('/referrals/program-info', undefined, ['referral']),
};
